// S5 Zeitleiste — Gantt-View via vis-timeline.
//
// Design choices:
// - Dynamic-import vis-timeline + the CSS via a module side-effect so they
//   stay out of the main chunk (only loaded when user opens the timeline tab).
// - One Timeline instance per mount; data updates feed through DataSet to
//   avoid full re-renders.
// - Drag-to-move / resize: editable.updateTime=true; horizontal drag fires a
//   PATCH to /api/tasks/:id with the new start_date/end_date (YYYY-MM-DD form).
// - Drag-between-phases: editable.updateGroup=true; dropping a bar on another
//   phase/task row fires a /reorder with the target phase_id (see handleMove).
// - Click on item -> open TaskModal (parent provides the modal stack).
// - Dependency arrows: SVG overlay (see DependencyArrows.tsx). The overlay
//   re-renders on rangechanged + window-resize + manual tick-bump after drag.
// - Today line: built-in showCurrentTime.
// - Milestones: tasks where is_milestone=true become type='point' with a
//   diamond CSS class.
// - Zoom-buttons map to fixed zoomMin/zoomMax windows.

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import {
  Calendar,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  Route,
} from "lucide-preact";
import type {
  Phase,
  ProjectGantt,
  ProjectWithCounts,
  Task,
} from "@juuplan/shared";
import type {
  DataItem,
  DataGroup,
  Timeline,
  TimelineItem,
  TimelineOptions,
} from "vis-timeline";
import { patchTask, reorderTask, useGanttData } from "../store/project";
import { computeCriticalPath } from "../lib/criticalPath";
import { matchesAllFilters, type FilterRule } from "../lib/viewState";
import { projectTaskToRecord } from "../lib/taskRecord";
import { findUser, useUsers } from "../store/users";
import { useLabels, labelsById } from "../store/labels";
import { TASK_STATUS_LABEL, TASK_STATUS_VAR } from "../lib/format";
import type { LabelItem } from "@juuplan/shared";
import { TaskModal } from "../components/TaskModal";
import { pickFg } from "../components/LabelChip";
import { botSuffix, userTitle } from "../components/BotBadge";
import { DependencyArrows } from "../components/DependencyArrows";

// High id range for phase parent rows + the per-phase background summary bar
// — must never collide with task ids (which start at 1 and grow).
const PHASE_BASE = 1_000_000;
const PHASE_SUMMARY_BASE = 2_000_000;

interface TooltipMeta {
  title: string;
  ticket: string | null;
  phaseName: string;
  statusLabel: string;
  statusVar: string;
  startISO: string;
  endISO: string;
  assignees: string[];
  subtaskCount: number;
  /** Pre-resolved label chips for the tooltip: { name, color } pairs. */
  labels: { name: string; color: string }[];
}

interface Props {
  project: ProjectWithCounts;
  /** Viewer-Rolle: kein Drag/Resize der Balken, TaskModal read-only. */
  readOnly?: boolean;
  /**
   * Shared view filters from Project.tsx. This view previously did NO filtering
   * at all, so the type/hide-done controls had no effect here (#872/#873). The
   * tasks are filtered once here, before GanttInner builds its vis-timeline
   * datasets — the inner component stays unaware of filtering.
   */
  filters?: FilterRule[];
}

type ZoomLevel = "day" | "week" | "month" | "quarter";

const DAY = 24 * 60 * 60 * 1000;

// Window sizes in milliseconds — clamp zoom around the visible window.
const ZOOM_WINDOWS: Record<ZoomLevel, number> = {
  day: 7 * DAY,
  week: 30 * DAY,
  month: 120 * DAY,
  quarter: 365 * DAY,
};

const ZOOM_BUTTONS: { id: ZoomLevel; label: string; icon: preact.JSX.Element }[] = [
  { id: "day", label: "Tag", icon: <Calendar size={14} aria-hidden="true" /> },
  { id: "week", label: "Woche", icon: <CalendarDays size={14} aria-hidden="true" /> },
  { id: "month", label: "Monat", icon: <CalendarRange size={14} aria-hidden="true" /> },
  { id: "quarter", label: "Quartal", icon: <CalendarClock size={14} aria-hidden="true" /> },
];

export function GanttView(props: Props): preact.JSX.Element {
  const { project, readOnly = false, filters } = props;
  const gantt = useGanttData(project.id);

  // Filter the payload once, using the same predicate as every other view.
  // Dependency arrows reference task ids, so any arrow pointing at a filtered-out
  // task is dropped too — otherwise vis-timeline would draw an arrow to nothing.
  const data = useMemo(() => {
    const raw = gantt.data;
    if (!raw || !filters || filters.length === 0) return raw;
    const tasks = raw.tasks.filter((t) =>
      matchesAllFilters(projectTaskToRecord(t), filters)
    );
    const visible = new Set(tasks.map((t) => t.id));
    return {
      ...raw,
      tasks,
      dependencies: raw.dependencies.filter(
        (d) => visible.has(d.task_id) && visible.has(d.depends_on_id)
      ),
    };
  }, [gantt.data, filters]);

  if (gantt.loading && !gantt.data) {
    return (
      <div class="rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-8 text-center text-sm text-[color:var(--color-on-surface-variant)]">
        Zeitleiste wird geladen…
      </div>
    );
  }

  if (gantt.error || !data) {
    return (
      <div class="rounded-lg border border-[color:var(--color-error-container)] bg-[color:var(--color-error-container)] p-4 text-sm text-[color:var(--color-on-error-container)]">
        Zeitleiste konnte nicht geladen werden: {gantt.error ?? "Unbekannter Fehler"}
      </div>
    );
  }

  if (data.phases.length === 0) {
    return (
      <div class="rounded-lg border border-dashed border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-6 py-12 text-center">
        <p class="text-base font-medium text-[color:var(--color-on-surface)]">
          Keine Phasen im Projekt
        </p>
        <p class="mt-1 text-xs text-[color:var(--color-on-surface-variant)]">
          Lege in der Hauptansicht eine Phase mit Aufgaben an, um die Zeitleiste zu nutzen.
        </p>
      </div>
    );
  }

  return (
    <>
      <p class="md:hidden text-xs text-[color:var(--color-on-surface-variant)] px-3 py-2">
        Gantt-Ansicht ist für größere Bildschirme optimiert — horizontal scrollen.
      </p>
      <div class="overflow-x-auto w-full min-w-0">
        <GanttInner project={project} data={data} readOnly={readOnly} />
      </div>
    </>
  );
}

function GanttInner(props: {
  project: ProjectWithCounts;
  data: ProjectGantt;
  readOnly?: boolean;
}): preact.JSX.Element {
  const { project, data, readOnly = false } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<Timeline | null>(null);
  const [openTaskId, setOpenTaskId] = useState<number | null>(null);
  const [overlayTick, setOverlayTick] = useState(0);
  // #71 critical-path highlight — default OFF so the normal view is unchanged.
  const [showCriticalPath, setShowCriticalPath] = useState(false);

  // Deep-link open: ?task=<id> in the URL opens the matching task modal
  // immediately (mirrors TableView's handling for the same query param).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("task");
    if (!raw) return;
    const id = Number.parseInt(raw, 10);
    if (Number.isNaN(id)) return;
    setOpenTaskId(id);
    params.delete("task");
    const query = params.toString();
    const next =
      window.location.pathname + (query ? `?${query}` : "") + window.location.hash;
    window.history.replaceState({}, "", next);
  }, [project.id]);
  // Re-export the inner container (vis-timeline mounts inside containerRef but
  // creates its own scroll-area). For the overlay we use the outer.
  const [overlayContainer, setOverlayContainer] = useState<HTMLElement | null>(null);

  // Touch the user-store so assignee names are available for tooltips.
  // The signal value isn't read directly in JSX, so we depend on items
  // explicitly on the user list — otherwise tooltips can stay stuck on
  // "#42" if the /api/users response arrives after the gantt data.
  const usersState = useUsers();
  const labelsState = useLabels(project.id);

  // Tooltip-meta map keyed by task id. Vis-timeline's tooltip template only
  // sees the item, so we read meta out of this ref via closure.
  const tooltipMetaRef = useRef<Map<number, TooltipMeta>>(new Map());

  // Drag-to-move-phase lookup tables, read (via .current) inside the vis-timeline
  // onMove callback which lives in the once-on-mount effect below. Refs keep the
  // callback pointed at the latest data without re-mounting the timeline.
  // - groupPhaseRef: group-id -> phase-id. Covers phase parent rows
  //   (PHASE_BASE + phase.id) AND per-task rows (task.id -> its phase). Lets us
  //   translate whatever row a bar was dropped onto into a target phase.
  // - taskPhaseRef: task-id -> its current phase-id (to detect an actual change).
  // - subtaskIdsRef: sub-tasks must stay in their parent's phase (server rejects
  //   a cross-phase move), so we never issue a reorder for them.
  const groupPhaseRef = useRef<Map<number, number>>(new Map());
  const taskPhaseRef = useRef<Map<number, number>>(new Map());
  const subtaskIdsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    const gp = new Map<number, number>();
    const tp = new Map<number, number>();
    const subs = new Set<number>();
    for (const p of data.phases) gp.set(PHASE_BASE + p.id, p.id);
    for (const t of data.tasks) {
      gp.set(t.id, t.phase_id);
      tp.set(t.id, t.phase_id);
      if (t.parent_id !== null) subs.add(t.id);
    }
    groupPhaseRef.current = gp;
    taskPhaseRef.current = tp;
    subtaskIdsRef.current = subs;
  }, [data.tasks, data.phases]);

  const items: DataItem[] = useMemo(() => {
    const built = buildItems(
      data.tasks,
      data.phases,
      project,
      tooltipMetaRef.current,
      labelsById(labelsState.items)
    );
    return built;
    // usersState.items + labelsState.items intentionally included so the
    // tooltip rebuilds when those resolve after the gantt payload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.tasks, data.phases, project, usersState.items, labelsState.items]);
  const groups: DataGroup[] = useMemo(
    () => buildGroups(data.phases, data.tasks, project),
    [data.phases, data.tasks, project]
  );
  // Tasks buildItems had to drop silently (#43) — surfaced as chips below the
  // timeline instead of just disappearing.
  const unplannedTasks: Task[] = useMemo(
    () => getUnplannedTasks(data.tasks),
    [data.tasks]
  );
  // #71 — longest dependency path, recomputed whenever the underlying data
  // changes. The toggle only decides whether we apply it as a selection.
  const criticalPath = useMemo(
    () => computeCriticalPath(data.tasks, data.dependencies),
    [data.tasks, data.dependencies]
  );

  // Mount / unmount the Timeline once. We update items/groups via setData
  // below to avoid re-creating the instance on every prop change.
  useEffect(() => {
    if (!containerRef.current) return;
    let cancelled = false;
    let cleanup: (() => void) | null = null;

    void (async () => {
      const vis = await import("vis-timeline/standalone");
      // CSS side-effect: only fetched when GanttView is mounted.
      await import("vis-timeline/styles/vis-timeline-graph2d.min.css");
      if (cancelled || !containerRef.current) return;

      const opts: TimelineOptions = {
        // vis-timeline sanitisiert sonst den Group-/Item-Content-HTML und
        // strippt dabei class + inline style — dadurch verschwanden der
        // Phasen-Swatch und unsere .juuplan-phase-name-Styles. Wir escapen alle
        // dynamischen Werte in den Templates selbst (escapeHtml), daher ist das
        // Abschalten des eingebauten Filters hier sicher.
        xss: { disabled: true },
        // Viewer (read-only): kein Drag/Resize der Balken.
        // updateGroup: Balken vertikal auf eine andere (Phasen-)Zeile ziehen
        // weist die Aufgabe der Zielphase zu (siehe handleMove). updateTime:
        // horizontales Ziehen ändert Start/Ende. Beide zusammen erlaubt.
        editable: { updateTime: !readOnly, updateGroup: !readOnly, add: false, remove: false, overrideItems: false },
        // Mirror item.id onto the DOM as data-id so DependencyArrows can locate
        // each bar. vis-timeline omits this attribute by default.
        dataAttributes: ["id"],
        // Let the timeline grow to fit content; page-level scroll handles
        // long task lists. (Previous maxHeight: 70vh caused content to be
        // clipped without a working internal scrollbar — vis-timeline's
        // internal vertical scroll didn't get the events on every device.)
        margin: { item: 4, axis: 6 },
        orientation: { axis: "top", item: "top" },
        stack: true,
        showCurrentTime: true,
        zoomMin: 2 * DAY,
        zoomMax: 5 * 365 * DAY,
        verticalScroll: true,
        horizontalScroll: true,
        zoomKey: "ctrlKey",
        locale: "de",
        format: {
          minorLabels: { day: "D", week: "[KW] w", month: "MMM" },
          majorLabels: { day: "MMMM YYYY", week: "MMMM YYYY", month: "YYYY" },
        },
        tooltip: {
          followMouse: true,
          overflowMethod: "flip",
          template: (item) => {
            // Items without a numeric task id (background/summary bars) get no
            // tooltip — vis falls back to the native title (empty here).
            const id = Number(item.id);
            if (!Number.isFinite(id)) return "";
            const meta = tooltipMetaRef.current.get(id);
            if (!meta) return "";
            return renderTooltip(meta);
          },
        },
        onMove: (item, callback) => {
          handleMove(
            item,
            groupPhaseRef.current,
            taskPhaseRef.current,
            subtaskIdsRef.current
          ).then(
            () => callback(item),
            () => callback(null)
          );
        },
        onMoving: (item, callback) => {
          // Snap to whole-day boundaries for nicer visual feedback.
          callback(item);
        },
      };

      const timeline = new vis.Timeline(
        containerRef.current,
        items,
        groups,
        opts
      );
      timelineRef.current = timeline;
      setOverlayContainer(containerRef.current);

      // Default view: fit to data, fall back to "today +/- 30d" if empty.
      const range = computeInitialRange(items);
      if (range) timeline.setWindow(range.start, range.end, { animation: false });

      const bump = (): void => setOverlayTick((t) => t + 1);
      timeline.on("rangechanged", bump);
      timeline.on("changed", bump);
      timeline.on("click", (ev) => {
        if (ev?.item != null) {
          const id = Number(ev.item);
          // Phase-summary background bars use ids >= PHASE_SUMMARY_BASE;
          // they aren't real tasks, so skip the modal for those.
          if (Number.isFinite(id) && id < PHASE_SUMMARY_BASE) setOpenTaskId(id);
        }
      });
      window.addEventListener("resize", bump);

      // First arrows draw after items have rendered (give the DOM one tick).
      requestAnimationFrame(bump);

      cleanup = () => {
        window.removeEventListener("resize", bump);
        timeline.destroy();
        timelineRef.current = null;
        setOverlayContainer(null);
      };
    })();

    return () => {
      cancelled = true;
      cleanup?.();
    };
    // intentionally only on mount: data flows in via setData below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push fresh items/groups whenever the data changes.
  useEffect(() => {
    const t = timelineRef.current;
    if (!t) return;
    t.setGroups(groups);
    t.setItems(items);
    requestAnimationFrame(() => setOverlayTick((x) => x + 1));
  }, [items, groups]);

  // #71 critical-path highlight: reuse vis-timeline's own "selected" style
  // (.vis-item.juuplan-task.vis-selected — box-shadow var(--color-primary),
  // already theme-aware) instead of inventing new CSS. `overlayContainer`
  // flips from null once the async timeline mount finishes, so it doubles as
  // the "timeline is ready" signal here. A detected cycle means "no highlight",
  // not "highlight something wrong".
  useEffect(() => {
    const t = timelineRef.current;
    if (!t) return;
    const ids =
      showCriticalPath && !criticalPath.hasCycle
        ? Array.from(criticalPath.taskIds)
        : [];
    t.setSelection(ids);
  }, [showCriticalPath, criticalPath, overlayContainer, items]);

  // Welche Zoomstufe zuletzt gewählt wurde. Vorher war das reine Fire-and-
  // forget-Logik: die Buttons sahen alle vier identisch aus, man konnte nicht
  // ablesen, ob Tag, Woche, Monat oder Quartal aktiv war.
  const [zoom, setZoomLevel] = useState<ZoomLevel | null>(null);

  const setZoom = (lvl: ZoomLevel): void => {
    setZoomLevel(lvl);
    const t = timelineRef.current;
    if (!t) return;
    const span = ZOOM_WINDOWS[lvl];
    const win = t.getWindow();
    const center = (win.start.getTime() + win.end.getTime()) / 2;
    t.setWindow(new Date(center - span / 2), new Date(center + span / 2));
  };

  const openTask = openTaskId != null ? data.tasks.find((t) => t.id === openTaskId) ?? null : null;

  return (
    <div class="flex flex-col gap-3">
      {/* Zoom toolbar */}
      <div class="flex flex-wrap items-center gap-1.5" role="group" aria-label="Zeitleisten-Zoom">
        {ZOOM_BUTTONS.map((z) => {
          const active = zoom === z.id;
          return (
            <button
              key={z.id}
              type="button"
              onClick={() => setZoom(z.id)}
              aria-pressed={active}
              class={`inline-flex items-center gap-1.5 rounded border px-2.5 py-1 text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors min-h-[28px] ${
                active
                  ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary-container)] text-[color:var(--color-on-primary-container)]"
                  : "border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)]"
              }`}
            >
              {z.icon}
              {z.label}
            </button>
          );
        })}
        <span class="ml-auto text-label text-[color:var(--color-on-surface-variant)]">
          Tipp: Strg + Mausrad zum Zoomen, Klick auf Balken öffnet Details.
        </span>
      </div>

      {/* #71 Critical-path toggle — only shown when there's actually a
          dependency graph to walk. Default OFF, normal view stays unchanged.
          Highlighting reuses vis-timeline's own selection style (setSelection
          below) instead of new CSS, so it's already theme-aware. */}
      {data.dependencies.length > 0 && (
        <div class="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => setShowCriticalPath((v) => !v)}
            aria-pressed={showCriticalPath}
            class={`inline-flex items-center gap-1.5 rounded border px-2.5 py-1 text-xs font-medium transition-colors min-h-[28px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 ${
              showCriticalPath
                ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary)] text-[color:var(--color-on-primary)]"
                : "border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)]"
            }`}
          >
            <Route size={14} aria-hidden="true" />
            Kritischen Pfad zeigen
          </button>
          {showCriticalPath && (
            <span class="text-xs text-[color:var(--color-on-surface-variant)]">
              {criticalPath.hasCycle
                ? "Zyklus in den Abhängigkeiten erkannt — kein kritischer Pfad ermittelbar."
                : criticalPath.taskIds.size > 0
                  ? `Kritischer Pfad: ${criticalPath.totalDays} ${criticalPath.totalDays === 1 ? "Tag" : "Tage"}`
                  : null}
            </span>
          )}
        </div>
      )}

      {/* Timeline container + SVG overlay */}
      <div class="relative rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)]">
        <div
          ref={containerRef}
          class="juuplan-gantt min-h-[400px]"
        />
        <DependencyArrows
          container={overlayContainer}
          tick={overlayTick}
          dependencies={data.dependencies}
        />
      </div>

      {/* Aufgaben ohne Start-/Enddatum: buildItems kann sie nicht platzieren,
          also verschwinden sie nicht kommentarlos, sondern landen hier als
          klickbare Chips (#43). Liegt bewusst außerhalb des Timeline-Containers,
          damit der harte Dark-Mode-Hintergrund von vis-timeline nicht mit
          hineinwirkt — normale Theme-Klassen reichen hier. */}
      {unplannedTasks.length > 0 && (
        <div class="rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-3">
          <div class="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <p class="text-sm font-medium text-[color:var(--color-on-surface)]">
              Ohne Datum
            </p>
            <span class="text-xs text-[color:var(--color-on-surface-variant)]">
              {unplannedTasks.length}{" "}
              {unplannedTasks.length === 1 ? "Aufgabe" : "Aufgaben"} ohne Datum
            </span>
          </div>
          <p class="mt-1 text-xs text-[color:var(--color-on-surface-variant)]">
            Diese Aufgaben haben kein Start- oder Enddatum und erscheinen deshalb
            nicht in der Zeitleiste. Aufgabe öffnen und ein Datum setzen, um sie
            einzuplanen.
          </p>
          <div class="mt-2 flex flex-wrap gap-1.5">
            {unplannedTasks.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setOpenTaskId(t.id)}
                class="inline-flex min-w-0 max-w-full items-center gap-1 rounded-full border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2.5 py-1 text-xs font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
              >
                {t.ticket && (
                  <span class="shrink-0 font-mono opacity-70">{t.ticket}</span>
                )}
                <span class="min-w-0 truncate">{t.title}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {openTask && (
        <TaskModal
          task={openTask}
          siblings={data.tasks}
          projectId={project.id}
          phases={project.phases}
          onClose={() => setOpenTaskId(null)}
          onDeleted={() => setOpenTaskId(null)}
          readOnly={readOnly}
        />
      )}
    </div>
  );
}

// ---------- helpers ----------

function toIsoDate(d: Date): string {
  // Local time, no TZ shift — we want the day the user dragged onto.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Sprint 3 Story 2: Primary-Assignee-Avatar als HTML-Snippet für vis-timeline
// item.content. Bei langen Bars wird der Avatar links inline gerendert, bei
// Milestones rechts neben dem Stripe (siehe weiter unten).
// vis-timeline 8.x sanitisiert HTML in `content` — `class` und `style`
// attributes werden komplett gestrippt. Wir nutzen daher nur HTML-attrs
// (`width`/`height`) die nicht gestripped werden, und matchen Selektoren
// in styles/index.css über die DOM-Position (`.vis-item-content > img`).
function renderAvatarHtml(userId: number | null): string {
  if (userId === null) return "";
  const u = findUser(userId);
  if (!u) return "";
  const initials = (u.name || "?")
    .split(/\s+/)
    .map((p) => p[0] ?? "")
    .slice(0, 2)
    .join("")
    .toUpperCase();
  // Bots: eigener Titel plus data-Attribut, das styles/index.css als
  // abweichenden Kreis rendert. `class`/`style` würde vis-timeline strippen.
  const title = escapeHtml(userTitle(userId, u.name));
  if (u.avatar_url) {
    return `<img width="22" height="22" src="${escapeHtml(u.avatar_url)}" alt="${escapeHtml(u.name)}" title="${title}" loading="lazy" />`;
  }
  // Fallback: span mit data-initials, CSS rendert ihn als runden Kreis.
  if (u.is_bot) {
    return `<span data-juuplan-avatar-fallback="1" data-juuplan-avatar-bot="1" title="${title}">BOT</span>`;
  }
  return `<span data-juuplan-avatar-fallback="1" title="${title}">${escapeHtml(initials)}</span>`;
}

// Tasks with none of start_date/end_date/deadline can't be placed on the
// timeline at all — buildItems silently drops them (see below). Exported so
// GanttInner can list them in the "ohne Datum" strip instead of just losing
// them, and so the predicate stays in exactly one place.
export function getUnplannedTasks(tasks: Task[]): Task[] {
  return tasks.filter((t) => !(t.start_date ?? t.end_date ?? t.deadline));
}

function buildItems(
  tasks: Task[],
  phases: Phase[],
  project: ProjectWithCounts,
  metaOut: Map<number, TooltipMeta>,
  labelMap: Map<number, LabelItem>
): DataItem[] {
  const phaseColor: Record<number, string> = {};
  const phaseName: Record<number, string> = {};
  for (const p of phases) {
    if (p.color) phaseColor[p.id] = p.color;
    phaseName[p.id] = p.name;
  }
  // Fall back to project.phases (older callsites) — phases param is the
  // source of truth for the current Gantt payload though.
  for (const p of project.phases) {
    if (!(p.id in phaseName)) {
      phaseName[p.id] = p.name;
      if (p.color) phaseColor[p.id] = p.color;
    }
  }
  // Ersatzfarbe, wenn eine Phase (oder das Projekt) keine eigene Farbe hat.
  // Vorher stand hier der Hex `#2563EB` — zufällig derselbe Wert, den
  // `--color-primary` im Hellmodus hat, aber ohne ihn zu referenzieren: im
  // Dunkelmodus blieb der Balken beim hellen Blau des Hellmodus stehen.
  const fallback = project.color ?? "var(--color-primary)";

  // Track each phase's task date-extent for the summary background bar.
  const phaseExtent = new Map<number, { min: number; max: number }>();
  const recordExtent = (phaseId: number, start: Date, end: Date): void => {
    const ext = phaseExtent.get(phaseId);
    if (!ext) {
      phaseExtent.set(phaseId, { min: start.getTime(), max: end.getTime() });
      return;
    }
    if (start.getTime() < ext.min) ext.min = start.getTime();
    if (end.getTime() > ext.max) ext.max = end.getTime();
  };

  // Clear & rebuild meta map.
  metaOut.clear();

  const out: DataItem[] = [];
  for (const t of tasks) {
    const color = phaseColor[t.phase_id] ?? fallback;
    const startStr = t.start_date ?? t.end_date ?? t.deadline;
    if (!startStr) continue; // task without dates: shown in the "ohne Datum" strip instead
    const start = new Date(startStr);
    if (Number.isNaN(start.getTime())) continue;

    const endStr = t.end_date ?? t.start_date ?? t.deadline;
    const endParsed = endStr ? new Date(endStr) : new Date(start.getTime() + DAY);
    if (Number.isNaN(endParsed.getTime())) continue;
    recordExtent(t.phase_id, start, endParsed);

    // Build tooltip metadata for every plottable task (milestones too).
    const assigneeNames: string[] = [];
    for (const uid of t.assignee_ids ?? []) {
      const u = findUser(uid);
      assigneeNames.push(u ? `${u.name}${botSuffix(uid)}` : `#${uid}`);
    }
    const labelChips: { name: string; color: string }[] = [];
    for (const lid of t.label_ids ?? []) {
      const l = labelMap.get(lid);
      if (l) labelChips.push({ name: l.name, color: l.color });
    }
    metaOut.set(t.id, {
      title: t.title,
      ticket: t.ticket,
      phaseName: phaseName[t.phase_id] ?? "—",
      statusLabel: TASK_STATUS_LABEL[t.status],
      statusVar: TASK_STATUS_VAR[t.status],
      startISO: toIsoDate(start),
      endISO: toIsoDate(endParsed),
      assignees: assigneeNames,
      subtaskCount: t.subtask_count ?? 0,
      labels: labelChips,
    });

    if (t.is_milestone) {
      // Asana-Stil für kurze Punkte/Milestones: Title + Avatar rechts neben
      // dem farbigen Stripe als externer Block. Vis-timeline rendert
      // `content` neben dem Punkt.
      const milestoneAvatar = renderAvatarHtml(t.primary_assignee_id);
      const milestoneTicket = t.ticket
        ? `<span class="juuplan-timeline-ticket" style="font-family:ui-monospace,monospace;font-size:0.85em;opacity:0.8;margin-right:3px">${escapeHtml(t.ticket)}</span>`
        : "";
      out.push({
        id: t.id,
        group: t.id,
        content: `<span class="juuplan-milestone-meta">${milestoneAvatar}${milestoneTicket}<span class="juuplan-milestone-title">${escapeHtml(t.title)}</span></span>`,
        start,
        type: "point",
        className: `juuplan-milestone juuplan-status-${t.status}`,
        style: `--juuplan-color: ${color}`,
      });
      continue;
    }

    // vis-timeline ranges should be exclusive on the end side; for "same-day"
    // we extend by 1 day so the bar is visible.
    const adjustedEnd =
      endParsed.getTime() <= start.getTime() ? new Date(start.getTime() + DAY) : endParsed;

    // Render up to 3 label dots inline after the title (vis-timeline allows
    // HTML in `content`). Anything beyond shows as a "+N" badge.
    let labelDots = "";
    if (labelChips.length > 0) {
      const shown = labelChips.slice(0, 3);
      const extra = labelChips.length - shown.length;
      labelDots =
        '<span class="juuplan-task-bar-labels">' +
        shown
          .map(
            (l) =>
              `<span class="juuplan-task-bar-label-dot" style="background:${escapeHtml(l.color)}" title="${escapeHtml(l.name)}"></span>`
          )
          .join("") +
        (extra > 0 ? `<span class="juuplan-task-bar-label-more">+${extra}</span>` : "") +
        "</span>";
    }
    const barAvatar = renderAvatarHtml(t.primary_assignee_id);
    const barTicket = t.ticket
      ? `<span class="juuplan-timeline-ticket" style="font-family:ui-monospace,monospace;font-size:0.85em;opacity:0.85;margin-right:3px">${escapeHtml(t.ticket)}</span>`
      : "";
    out.push({
      id: t.id,
      group: t.id,
      content: `${barAvatar}${barTicket}<span class="juuplan-task-bar-title">${escapeHtml(t.title)}</span>${labelDots}`,
      start,
      end: adjustedEnd,
      type: "range",
      className: `juuplan-task juuplan-status-${t.status}`,
      style: `background: ${color}; border-color: ${color}; color: ${barFg(color)}`,
    });
  }

  // Phase-summary bars: one background item per phase, sitting on the phase
  // parent row. Spans from min(task.start) to max(task.end ?? task.start).
  for (const p of phases) {
    const ext = phaseExtent.get(p.id);
    if (!ext) continue;
    const color = phaseColor[p.id] ?? fallback;
    const startD = new Date(ext.min);
    const endD = new Date(ext.max);
    const days = Math.max(
      1,
      Math.round((endD.getTime() - startD.getTime()) / DAY)
    );
    out.push({
      id: PHASE_SUMMARY_BASE + p.id,
      group: PHASE_BASE + p.id,
      content: `${escapeHtml(p.name)} · ${days} Tage`,
      start: startD,
      end: endD,
      type: "background",
      className: "juuplan-phase-summary",
      style: `background: ${color}`,
    });
  }

  return out;
}

function buildGroups(
  phases: Phase[],
  tasks: Task[],
  project: ProjectWithCounts
): DataGroup[] {
  const out: (DataGroup & { order?: number })[] = [];
  // Bucket tasks by phase, preserve task sort_order so child rows feel stable.
  const tasksByPhase = new Map<number, Task[]>();
  for (const t of tasks) {
    const arr = tasksByPhase.get(t.phase_id) ?? [];
    arr.push(t);
    tasksByPhase.set(t.phase_id, arr);
  }
  for (const arr of tasksByPhase.values()) {
    arr.sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
  }

  // Phase parents live in the PHASE_BASE id range (declared module-level)
  // so they never collide with task ids.
  for (const [phaseIdx, p] of phases.entries()) {
    const childTasks = tasksByPhase.get(p.id) ?? [];
    out.push({
      id: PHASE_BASE + p.id,
      content: phaseGroupTemplate(p),
      order: (p.sort_order ?? phaseIdx) * 1000,
      style: `--juuplan-phase-color: ${p.color ?? project.color ?? "var(--color-primary)"}`,
      className: "juuplan-phase-group",
      nestedGroups: childTasks.map((t) => t.id),
      showNested: true,
    });
    for (const [tIdx, t] of childTasks.entries()) {
      out.push({
        id: t.id,
        content: taskGroupTemplate(t, p),
        order: (p.sort_order ?? phaseIdx) * 1000 + tIdx + 1,
        className: "juuplan-task-group",
      });
    }
  }
  return out as DataGroup[];
}

function phaseGroupTemplate(phase: Phase): string {
  const swatch = phase.color ?? "var(--color-primary)";
  const escapedName = escapeHtml(phase.name);
  return (
    `<div class="juuplan-phase-row" title="${escapedName}">` +
    `<span class="juuplan-phase-swatch" style="background:${swatch}"></span>` +
    `<span class="juuplan-phase-name">${escapedName}</span>` +
    `</div>`
  );
}

function taskGroupTemplate(task: Task, phase: Phase): string {
  const dot = phase.color ?? "var(--color-primary)";
  const ticket = task.ticket
    ? `<span class="juuplan-task-ticket">${escapeHtml(task.ticket)}</span>`
    : "";
  return (
    `<div class="juuplan-task-label">` +
    `<span class="juuplan-task-dot" style="background:${dot}"></span>` +
    ticket +
    `<span class="juuplan-task-name" title="${escapeHtml(task.title)}">${escapeHtml(task.title)}</span>` +
    `</div>`
  );
}

/**
 * Vordergrundfarbe für einen Gantt-Balken. `pickFg` rechnet die Luminanz eines
 * HEX aus — eine CSS-Variable kann es nicht lesen und fällt still auf Weiß
 * zurück. Für Token-Farben nehmen wir darum gleich `--color-on-accent`, statt
 * uns auf diesen stillen Rückfall zu verlassen (er wäre hier zufällig richtig,
 * aber niemand könnte das dem Code ansehen). Nutzerfarben sind weiter HEX und
 * gehen unverändert durch `pickFg`.
 */
function barFg(color: string): string {
  return color.startsWith("var(") ? "var(--color-on-accent)" : pickFg(color);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatDeDate(iso: string): string {
  // toIsoDate output (YYYY-MM-DD) → DD.MM.YYYY for the tooltip.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function renderTooltip(m: TooltipMeta): string {
  const dateRange =
    m.startISO === m.endISO
      ? formatDeDate(m.startISO)
      : `${formatDeDate(m.startISO)} – ${formatDeDate(m.endISO)}`;
  const assignees =
    m.assignees.length > 0
      ? escapeHtml(m.assignees.join(", "))
      : "<span class=\"juuplan-gantt-tt-muted\">—</span>";
  const subtaskRow =
    m.subtaskCount > 0
      ? `<div class="juuplan-gantt-tt-row"><span class="juuplan-gantt-tt-label">Unteraufgaben</span><span>${m.subtaskCount}</span></div>`
      : "";
  const labelsRow =
    m.labels.length > 0
      ? `<div class="juuplan-gantt-tt-row"><span class="juuplan-gantt-tt-label">Labels</span><span class="juuplan-gantt-tt-labels">${m.labels
          .map(
            (l) =>
              `<span class="juuplan-gantt-tt-label-chip" style="background:${escapeHtml(l.color)}">${escapeHtml(l.name)}</span>`
          )
          .join("")}</span></div>`
      : "";
  return (
    `<div class="juuplan-gantt-tt">` +
    `<div class="juuplan-gantt-tt-title">${escapeHtml(m.title)}</div>` +
    (m.ticket
      ? `<div class="juuplan-gantt-tt-row"><span class="juuplan-gantt-tt-label">Ticket</span><span>${escapeHtml(m.ticket)}</span></div>`
      : "") +
    `<div class="juuplan-gantt-tt-row"><span class="juuplan-gantt-tt-label">Phase</span><span>${escapeHtml(m.phaseName)}</span></div>` +
    `<div class="juuplan-gantt-tt-row"><span class="juuplan-gantt-tt-label">Zeitraum</span><span>${escapeHtml(dateRange)}</span></div>` +
    `<div class="juuplan-gantt-tt-row"><span class="juuplan-gantt-tt-label">Status</span><span class="juuplan-gantt-tt-status" style="background:${m.statusVar}">${escapeHtml(m.statusLabel)}</span></div>` +
    `<div class="juuplan-gantt-tt-row"><span class="juuplan-gantt-tt-label">Zugewiesen</span><span>${assignees}</span></div>` +
    labelsRow +
    subtaskRow +
    `</div>`
  );
}

function computeInitialRange(items: DataItem[]): { start: Date; end: Date } | null {
  let min = Infinity;
  let max = -Infinity;
  for (const it of items) {
    const start = it.start instanceof Date ? it.start.getTime() : new Date(it.start).getTime();
    const endRaw = it.end ?? it.start;
    const end = endRaw instanceof Date ? endRaw.getTime() : new Date(endRaw as string).getTime();
    if (Number.isFinite(start) && start < min) min = start;
    if (Number.isFinite(end) && end > max) max = end;
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    const now = Date.now();
    return { start: new Date(now - 30 * DAY), end: new Date(now + 30 * DAY) };
  }
  // Pad by 7 days each side.
  return { start: new Date(min - 7 * DAY), end: new Date(max + 7 * DAY) };
}

async function handleMove(
  item: TimelineItem,
  groupPhase: Map<number, number>,
  taskPhase: Map<number, number>,
  subtaskIds: Set<number>
): Promise<void> {
  const id = Number(item.id);
  if (!Number.isFinite(id)) return;
  const startDate = item.start instanceof Date ? item.start : new Date(item.start);

  // Cross-phase move: item.group is the row the bar was dropped onto — either a
  // phase parent row (PHASE_BASE + phaseId) or a sibling task row. Both resolve
  // to a target phase via groupPhase. We only reorder when the phase actually
  // changed and the task isn't a sub-task (those are pinned to the parent phase).
  const targetGroup = Number(item.group);
  const targetPhase = groupPhase.get(targetGroup);
  const currentPhase = taskPhase.get(id);
  const phaseChanged =
    targetPhase != null &&
    currentPhase != null &&
    targetPhase !== currentPhase &&
    !subtaskIds.has(id);

  // Date change (unchanged behaviour). For milestones we only set start_date;
  // for ranges we set both.
  if (item.type === "point") {
    await patchTask(id, { start_date: toIsoDate(startDate), end_date: toIsoDate(startDate) });
  } else {
    const endRaw = item.end;
    if (endRaw == null) {
      await patchTask(id, { start_date: toIsoDate(startDate) });
    } else {
      const endDate = endRaw instanceof Date ? endRaw : new Date(endRaw as string);
      await patchTask(id, {
        start_date: toIsoDate(startDate),
        end_date: toIsoDate(endDate),
      });
    }
  }

  if (phaseChanged && targetPhase != null) {
    // sort_order 0 → drop at the top of the target phase.
    await reorderTask(id, { phase_id: targetPhase, sort_order: 0 });
  }
}
